import { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { localize } from '../../i18n/locale';
import { executePortOnePayment } from '../../payments/portoneClient';
import {
    getPaymentAnalyticsContext,
    trackConfirmedPurchase,
    trackEvent,
    trackEventOnce,
} from '../../utils/analytics';


const buttonStyle = {
    width: '100%',
    border: 0,
    borderRadius: '12px',
    padding: '0.9rem 1rem',
    fontWeight: 700,
    cursor: 'pointer',
};


export default function DomesticPaymentOptions({
    language,
    market,
    product,
    productId,
    resultId,
    guestPurchase,
    returnTo,
    tossProcessing,
    onToss,
}) {
    const navigate = useNavigate();
    const tr = useCallback((ko, en) => localize(language, { ko, en }), [language]);
    const processingRef = useRef(false);
    const [processing, setProcessing] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const disabled = processing || tossProcessing;

    const startPortOne = async () => {
        if (processingRef.current || tossProcessing) return;
        processingRef.current = true;
        setProcessing(true);
        setError('');
        setMessage(tr('카드 결제를 준비하고 있습니다.', 'Preparing card payment.'));

        try {
            const { order, confirmation } = await executePortOnePayment({
                productId,
                resultId,
                onOrderCreated: (createdOrder) => {
                    trackEvent('begin_checkout', {
                        currency: 'KRW',
                        value: createdOrder.total_amount,
                        payment_provider: 'portone',
                        pg_provider: 'nhn_kcp',
                        product_id: productId,
                        package_type: 'chemistry_pass',
                        market,
                        source_page: 'payment_start',
                        ...getPaymentAnalyticsContext(productId),
                    });
                    trackEventOnce('payment_start', `portone:${createdOrder.payment_id}`, {
                        currency: 'KRW',
                        value: createdOrder.total_amount,
                        payment_provider: 'portone',
                        pg_provider: 'nhn_kcp',
                        market,
                        source_page: 'payment_start',
                        ...getPaymentAnalyticsContext(productId),
                    });
                    setMessage(tr('카드 결제를 진행해 주세요.', 'Complete the card payment.'));
                },
            });

            setMessage(tr('결제가 확인되었습니다.', 'Payment confirmed.'));
            window.dispatchEvent(new Event('premium-entitlements-updated'));
            const verifiedProductId = confirmation.product_id || productId;
            trackConfirmedPurchase(
                { status: 'confirmed', order_id: confirmation.payment_id },
                {
                    currency: 'KRW',
                    value: order.total_amount,
                    payment_provider: 'portone',
                    pg_provider: 'nhn_kcp',
                    market,
                    product_id: verifiedProductId,
                    ...getPaymentAnalyticsContext(verifiedProductId),
                    items: [{
                        item_id: verifiedProductId,
                        item_name: verifiedProductId,
                        price: order.total_amount,
                        quantity: 1,
                    }],
                },
            );
            navigate(returnTo || product?.route || '/mypage', { replace: true });
        } catch (checkoutError) {
            const failureReason = checkoutError?.failureReason || (
                Number(checkoutError?.status) === 0 ? 'network_error' : 'verification_failed'
            );
            trackEvent('payment_failed', {
                currency: 'KRW',
                value: product?.amount,
                payment_provider: 'portone',
                pg_provider: 'nhn_kcp',
                product_id: productId,
                market,
                failure_reason: failureReason,
                failure_stage: failureReason === 'verification_failed' ? 'confirmation' : 'provider_open',
                ...getPaymentAnalyticsContext(productId),
            });
            setMessage('');
            setError(
                failureReason === 'user_cancelled'
                    ? tr('결제가 취소되었습니다. 다시 시도할 수 있습니다.', 'Payment was cancelled. You can try again.')
                    : failureReason === 'timeout'
                        ? tr('결제 시간이 초과되었습니다. 다시 시도해 주세요.', 'Payment timed out. Please try again.')
                        : failureReason === 'configuration_error'
                            ? tr('카드 결제 설정이 완료되지 않았습니다.', 'Card payment is not configured.')
                            : tr('결제를 확인하지 못했습니다. 다시 시도해 주세요.', 'Could not verify the payment. Please try again.'),
            );
        } finally {
            processingRef.current = false;
            setProcessing(false);
        }
    };

    return (
        <div style={{ marginTop: '1.5rem' }}>
            <p style={{ color: '#f8fafc', fontWeight: 700 }}>{product.title_en || product.title}</p>
            <p style={{ color: '#cbd5e1' }}>
                {tr('결제수단을 선택해 주세요.', 'Choose a payment method.')}
            </p>
            <div style={{ display: 'grid', gap: '0.75rem', marginTop: '1rem' }}>
                <button
                    type="button"
                    disabled={disabled}
                    onClick={startPortOne}
                    style={{ ...buttonStyle, background: '#f8fafc', color: '#0f172a', opacity: disabled ? 0.65 : 1 }}
                >
                    {processing
                        ? tr('결제 진행 중...', 'Payment in progress...')
                        : tr('신용·체크카드', 'Credit or debit card')}
                </button>
                {!guestPurchase ? (
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={onToss}
                        style={{ ...buttonStyle, background: '#3182f6', color: '#fff', opacity: disabled ? 0.65 : 1 }}
                    >
                        {tr('토스페이먼츠', 'Toss Payments')}
                    </button>
                ) : null}
            </div>
            {message ? <p aria-live="polite" style={{ color: '#bfdbfe' }}>{message}</p> : null}
            {error ? <p role="alert" style={{ color: '#fecaca' }}>{error}</p> : null}
        </div>
    );
}

DomesticPaymentOptions.propTypes = {
    language: PropTypes.string.isRequired,
    market: PropTypes.string.isRequired,
    product: PropTypes.shape({
        amount: PropTypes.number,
        route: PropTypes.string,
        title: PropTypes.string,
        title_en: PropTypes.string,
    }).isRequired,
    productId: PropTypes.string.isRequired,
    resultId: PropTypes.string,
    guestPurchase: PropTypes.bool.isRequired,
    returnTo: PropTypes.string,
    tossProcessing: PropTypes.bool.isRequired,
    onToss: PropTypes.func.isRequired,
};
